import React from 'react';
import {
  Button,
  ButtonProps,
  IconButton,
} from '@dropbox/dig-components/buttons';
import { useSignatureRequestContext } from 'signer-app/signature-request/context';
import {
  useSignerAppParams,
  useSignerContext,
} from 'signer-app/signer-experience/signer-experience';
import { FormattedMessage } from 'react-intl';
import styles from 'signer-app/signer-experience/next-button.module.css';
import { useHistory } from 'react-router';
import classNames from 'classnames';
import { VisuallyHidden } from '@dropbox/dig-foundations';
import { useShouldShowDrawer } from 'signer-app/utils/use-should-show-drawer';
import { useFeatureFlag } from 'signer-app/context/feature-flags';
import { findNextFieldWithError } from 'signer-app/signer-experience/find-next-field-with-error';
import { useTouchedFieldIds } from 'signer-app/signer-experience/use-validation';

interface Props {
  fullWidth?: boolean;
  size?: ButtonProps['size'];
  children?: React.ReactNode;
  icon?: boolean;
  className?: string;
}

export const NextButton: React.FC<Props> = ({
  fullWidth,
  size,
  children,
  icon = false,
  className,
}) => {
  const { selectedFieldIds } = useSignatureRequestContext();
  const { step, signatureRequestId } = useSignerAppParams();
  const {
    selectField,
    attachmentSlots,
    signerFields,
    validationErrors,
    requiredFieldsLeft,
    allValid,
  } = useSignerContext();

  const [activeField] = selectedFieldIds;
  const history = useHistory();
  const isUsingDrawer = useShouldShowDrawer();
  const fixNextAfterInsertEverywhere = useFeatureFlag(
    'sign_core_2026_07_07_fix_next_after_insert_everywhere',
  );
  const touchedFieldIds = useTouchedFieldIds();

  const visibleSignerFields = React.useMemo(
    () => signerFields.filter((f) => !f.hidden),
    [signerFields],
  );

  const nextRequired = React.useMemo(() => {
    if (!fixNextAfterInsertEverywhere) return false;
    if (requiredFieldsLeft === 0 || allValid) return false;

    const allFilledFieldsTouched = visibleSignerFields
      .filter((f) => !validationErrors[f.id])
      .every((f) => touchedFieldIds.has(f.id));
    return allFilledFieldsTouched;
  }, [
    fixNextAfterInsertEverywhere,
    requiredFieldsLeft,
    allValid,
    visibleSignerFields,
    validationErrors,
    touchedFieldIds,
  ]);

  const navigateToAttachments = () => {
    const attachmentsPath = `/sign/${signatureRequestId}/attachments/`;
    history.push(attachmentsPath);
  };

  const handleNext = () => {
    if (step === 'attachments') {
      // select first field
      selectField(signerFields[0].id);
      return;
    }

    if (nextRequired) {
      const field = findNextFieldWithError(
        visibleSignerFields,
        activeField,
        validationErrors,
        { groupAware: true },
      );
      if (field) {
        selectField(field.id);
        return;
      }
    }

    const index = signerFields.findIndex((f) => f.id === activeField);

    if (
      isUsingDrawer &&
      attachmentSlots.length > 0 &&
      index === signerFields.length - 1
    ) {
      navigateToAttachments();
      return;
    }

    const nextField = signerFields[(index + 1) % signerFields.length];
    selectField(nextField.id);
  };

  if (icon) {
    return (
      <>
        <IconButton
          className={classNames(styles.nextButton, className, styles.small)}
          variant="outline"
          size={size}
          onClick={handleNext}
          aria-labelledby="next-button-label"
          data-testid="button-next"
        >
          {children}
        </IconButton>
        <div id="next-button-label">
          <VisuallyHidden>
            <FormattedMessage
              id="e68dfb13e9194b0ab2b9282009d009eb9089ad52d6ce994f0f12fba939b0d050"
              defaultMessage="Next"
              description="Next button in the signer experience."
            />
          </VisuallyHidden>
        </div>
      </>
    );
  }

  return (
    <Button
      className={classNames(styles.nextButton, className)}
      variant="outline"
      fullWidth={fullWidth}
      size={size}
      onClick={handleNext}
      data-testid="button-next"
    >
      <FormattedMessage
        id="e68dfb13e9194b0ab2b9282009d009eb9089ad52d6ce994f0f12fba939b0d050"
        defaultMessage="Next"
        description="Next button in the signer experience."
      />
    </Button>
  );
};
